/**
 * DLE 評価タブの設問別コメントパネル（radar を囲む 4 象限）。
 *
 * 軸と象限の対応は **左上=一次回答 / 左下=壁打ち / 右上=返信速度 / 右下=依頼意向** で固定。
 * radar の頂点の並び（上から時計回りに 一次回答 → 壁打ち → 返信速度 → 依頼意向）とは
 * 一致しないが、読む順（設問 1→4）を優先した。左列を上から、右列を上から読むと
 * 設問順になる。
 *
 * 表示するのは**コメント本文だけ**で、回答者は出さない。API も回答者を返していない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価タブの設問別コメントを radar の 4 象限に配置して表示する
 */
import { useTranslation } from 'react-i18next';
import { DLE_SCORE_AXES, type DleScorePoint } from '../../../types/dle-evaluation-score.js';

/** コメント 1 件（軸色の縦罫 + 本文）。 @graph-connects none */
function DleCommentItem({ text, color }: { text: string; color: string }) {
  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2.5 py-2"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--color-text-primary)]">
        {text}
      </p>
    </div>
  );
}

/**
 * 1 軸ぶんのコメント（ラベル + 件数 + 明細）。
 *
 * 長いコメントが 1 件あるだけで radar がはみ出すので、パネル内でスクロールさせる。
 * @graph-connects none
 */
export function DleCommentPanel({
  axisIndex,
  point,
}: {
  /** `DLE_SCORE_AXES` の index */
  axisIndex: number;
  point: DleScorePoint | null;
}) {
  const { t } = useTranslation();
  const axis = DLE_SCORE_AXES[axisIndex];
  if (!axis) return null;
  const items = point?.comments[axis.key] ?? [];
  return (
    <section className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span
          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider"
          style={{
            background: `color-mix(in srgb, ${axis.color} 18%, transparent)`,
            color: axis.color,
          }}
        >
          {t(axis.labelKey)}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-[var(--color-text-muted)]">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{t('dleEvaluation.commentsEmpty')}</p>
      ) : (
        <div className="flex max-h-[320px] flex-col gap-2 overflow-y-auto pr-1">
          {items.map((text, index) => (
            <DleCommentItem
              key={`${String(index)}-${text.slice(0, 16)}`}
              text={text}
              color={axis.color}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * 左列（設問 1 上 / 設問 2 下）または右列（設問 3 上 / 設問 4 下）。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DLE 評価タブの設問別コメント 2 件ぶんの縦並び（radar の左右に置く列）
 * @graph-connects DleCommentPanel [calls] 1 軸ぶんのコメント明細描画
 */
export function DleCommentColumn({
  topAxisIndex,
  bottomAxisIndex,
  point,
}: {
  topAxisIndex: number;
  bottomAxisIndex: number;
  point: DleScorePoint | null;
}) {
  return (
    // h-full + justify-between で、グリッド行の高さ（= 中央の radar）いっぱいに広げて
    // 上下に振り分ける。これが無いと両方が上端に寄って「象限」に見えない。
    <div className="flex h-full min-w-0 flex-col justify-between gap-5">
      <DleCommentPanel axisIndex={topAxisIndex} point={point} />
      <DleCommentPanel axisIndex={bottomAxisIndex} point={point} />
    </div>
  );
}
